import React, { useState } from 'react';
import useIntegratePOSToastStore from '../../../stores/integratePOSToastStore';
import useIntegratePOSHeartlandRestaurantStore from '../../../stores/integratePOSHeartlandRestaurantStore';
import useIntegratePOSHeartlandRetailStore from '../../../stores/integratePOSHeartlandRetailStore';
import useIntegratePOSSquareStore from '../../../stores/integratePOSSquareStore';
import { connect } from 'react-redux';
import axios from 'axios';
import { getDefaultAxiosConfig } from '../requests/utilities/requests';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Import Toast components
import {
  ToastPrerequisitesContent,
  ToastAPICredentialsContent,
  ToastCredentialInputContent
} from './ToastIntegrationComponents';

// Import Heartland Restaurant components
import {
  HeartlandPrerequisitesContent,
  HeartlandAPISetupContent,
  HeartlandCredentialInputContent
} from './HeartlandRestaurantIntegrationComponents';

// Import Heartland Retail components
import {
  HeartlandRetailPrerequisitesContent,
  HeartlandRetailAPISetupContent,
  HeartlandRetailCredentialInputContent
} from './HeartlandRetailIntegrationComponents';

// Import Square components
import {
  SquarePrerequisitesContent,
  SquareAppSetupContent,
  SquareCredentialInputContent
} from './SquareIntegrationComponents';

// Updated POS Selection Content - renders all POS systems together
const POSSelectionContent = ({ onNext, onClose }) => {
  const [selectedPOS, setSelectedPOS] = useState('');

  const posSystemOptions = [
    {
      id: 'toast',
      name: 'Toast',
      description: 'Cloud-based restaurant management platform',
      supported: true,
      logo: '🍞',
      category: 'restaurant'
    },
    {
      id: 'heartland-restaurant',
      name: 'Heartland Restaurant',
      description: 'Complete restaurant POS and payment processing',
      supported: true,
      logo: '🍽️',
      category: 'restaurant'
    },
    {
      id: 'heartland-retail',
      name: 'Heartland Retail',
      description: 'Retail POS and inventory management solution',
      supported: true,
      logo: '🛍️',
      category: 'retail'
    },
    {
      id: 'square',
      name: 'Square',
      description: 'All-in-one payment and business management platform',
      supported: true,
      logo: '⏹️',
      category: 'universal'
    }
  ];

  const handlePOSSelection = (posId) => {
    if (posSystemOptions.find(pos => pos.id === posId)?.supported) {
      setSelectedPOS(posId);
    }
  };

  const handleNext = () => {
    if (selectedPOS) {
      onNext(selectedPOS);
    }
  };

  return (
    <div className="p-6 relative">
      {/* Close Icon */}
      <IconButton
        aria-label="Close POS setup"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Select Your POS System
        </h2>
        <p className="text-gray-600">
          Choose the POS system you'd like to integrate with Rightwork
        </p>
      </div>

      <div className="mb-8">
        {/* Render all POS systems in a single list */}
        <div className="space-y-3">
          {posSystemOptions.map((pos) => (
            <button
              type="button"
              aria-pressed={selectedPOS === pos.id}
              key={pos.id}
              onClick={() => handlePOSSelection(pos.id)}
              className={`
                w-full text-left relative border-2 rounded-2xl p-4 transition-all duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700
                ${selectedPOS === pos.id && pos.supported
                  ? 'border-violet-700 bg-violet-50'
                  : pos.supported
                  ? 'border-gray-200 hover:border-gray-300 hover:shadow-md bg-white'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <div className="text-2xl mr-3">{pos.logo}</div>
                  <div>
                    <h3 className={`text-base font-semibold ${selectedPOS === pos.id && pos.supported ? 'text-violet-900' : 'text-gray-900'}`}>
                      {pos.name}
                    </h3>
                    <p className={`text-sm mt-1 ${selectedPOS === pos.id && pos.supported ? 'text-violet-700' : 'text-gray-600'}`}>
                      {pos.description}
                    </p>
                  </div>
                </div>

                {selectedPOS === pos.id && pos.supported && (
                  <div className="flex-shrink-0 ml-4">
                    <div className="w-5 h-5 bg-violet-700 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <div className="flex">
            <svg className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-amber-800 font-medium">More POS integrations available!</p>
              <p className="text-xs text-amber-700 mt-1">
                We support multiple POS systems across different industries. Contact us if you need a specific integration not listed here.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedPOS}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            selectedPOS
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

const ConnectPOSModal = (props) => {
  const { open, onClose, companyId, onIntegrationComplete } = props;
  const [selectedPOS, setSelectedPOS] = useState('');
  const [currentStep, setCurrentStep] = useState('selection');
  const [toastSubmitting, setToastSubmitting] = useState(false);

  // Get reset functions from all stores
  const { resetForm: resetToastForm } = useIntegratePOSToastStore();
  const { resetForm: resetHeartlandRestaurantForm } = useIntegratePOSHeartlandRestaurantStore();
  const { resetForm: resetHeartlandRetailForm } = useIntegratePOSHeartlandRetailStore();
  const { resetForm: resetSquareForm } = useIntegratePOSSquareStore();

  const handleClose = () => {
    if (toastSubmitting) return;
    setCurrentStep('selection');
    setSelectedPOS('');
    resetToastForm();
    resetHeartlandRestaurantForm();
    resetHeartlandRetailForm();
    resetSquareForm();
    onClose();
  };

  const handlePOSSelection = (posId) => {
    setSelectedPOS(posId);

    // Navigate to the appropriate flow based on selected POS
    switch (posId) {
      case 'toast':
        setCurrentStep('toast-prerequisites');
        break;
      case 'heartland-restaurant':
        setCurrentStep('heartland-restaurant-prerequisites');
        break;
      case 'heartland-retail':
        setCurrentStep('heartland-retail-prerequisites');
        break;
      case 'square':
        setCurrentStep('square-prerequisites');
        break;
      default:
        setCurrentStep('selection');
    }
  };

  const handleBackToSelection = () => {
    setCurrentStep('selection');
    setSelectedPOS('');
    resetToastForm();
    resetHeartlandRestaurantForm();
    resetHeartlandRetailForm();
    resetSquareForm();
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'selection':
        return <POSSelectionContent onNext={handlePOSSelection} onClose={handleClose} />;

      // Toast Integration Flow
      case 'toast-prerequisites':
        return <ToastPrerequisitesContent
          onNext={() => setCurrentStep('toast-api-credentials')}
          onBack={handleBackToSelection}
          onClose={handleClose}
        />;
      case 'toast-api-credentials':
        return <ToastAPICredentialsContent
          onNext={() => setCurrentStep('toast-credential-input')}
          onBack={() => setCurrentStep('toast-prerequisites')}
          onClose={handleClose}
        />;
      case 'toast-credential-input':
        return <ToastCredentialInputContent
          onBack={() => setCurrentStep('toast-api-credentials')}
          onClose={handleClose}
          companyId={companyId}
          onIntegrationComplete={onIntegrationComplete}
          onSubmittingChange={setToastSubmitting}
        />;

      // Heartland Restaurant Integration Flow
      case 'heartland-restaurant-prerequisites':
        return <HeartlandPrerequisitesContent
          onNext={() => setCurrentStep('heartland-restaurant-api-setup')}
          onBack={handleBackToSelection}
          onClose={handleClose}
        />;
      case 'heartland-restaurant-api-setup':
        return <HeartlandAPISetupContent
          onNext={() => setCurrentStep('heartland-restaurant-credential-input')}
          onBack={() => setCurrentStep('heartland-restaurant-prerequisites')}
          onClose={handleClose}
        />;
      case 'heartland-restaurant-credential-input':
        return <HeartlandCredentialInputContent
          onBack={() => setCurrentStep('heartland-restaurant-api-setup')}
          onClose={handleClose}
          companyId={companyId}
          onIntegrationComplete={onIntegrationComplete}
        />;

      // Heartland Retail Integration Flow
      case 'heartland-retail-prerequisites':
        return <HeartlandRetailPrerequisitesContent
          onNext={() => setCurrentStep('heartland-retail-api-setup')}
          onBack={handleBackToSelection}
          onClose={handleClose}
        />;
      case 'heartland-retail-api-setup':
        return <HeartlandRetailAPISetupContent
          onNext={() => setCurrentStep('heartland-retail-credential-input')}
          onBack={() => setCurrentStep('heartland-retail-prerequisites')}
          onClose={handleClose}
        />;
      case 'heartland-retail-credential-input':
        return <HeartlandRetailCredentialInputContent
          onBack={() => setCurrentStep('heartland-retail-api-setup')}
          onClose={handleClose}
          companyId={companyId}
          onIntegrationComplete={onIntegrationComplete}
        />;

      // Square Integration Flow
      case 'square-prerequisites':
        return <SquarePrerequisitesContent
          onNext={() => setCurrentStep('square-app-setup')}
          onBack={handleBackToSelection}
          onClose={handleClose}
        />;
      case 'square-app-setup':
        return <SquareAppSetupContent
          onNext={() => setCurrentStep('square-credential-input')}
          onBack={() => setCurrentStep('square-prerequisites')}
          onClose={handleClose}
        />;
      case 'square-credential-input':
        return <SquareCredentialInputContent
          onBack={() => setCurrentStep('square-app-setup')}
          onClose={handleClose}
          companyId={companyId}
          onIntegrationComplete={onIntegrationComplete}
        />;

      default:
        return <POSSelectionContent onNext={handlePOSSelection} onClose={handleClose} />;
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => { if (reason === 'escapeKeyDown') handleClose(); }}
      aria-label="Connect POS"
      aria-describedby="connect-pos-modal-description"
      disableEscapeKeyDown={false}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 32px)',
          maxWidth: 700,
          bgcolor: 'background.paper',
          borderRadius: 6,
          boxShadow: 24,
          outline: 'none',
          maxHeight: '96vh',
          overflow: 'auto'
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Connect POS"
      >
        <p id="connect-pos-modal-description" className="sr-only">Choose your POS and follow the setup steps. Escape cancels setup unless credentials are being submitted.</p>
        {renderContent()}
      </Box>
    </Modal>
  );
};

export default ConnectPOSModal;
